/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import { useKPIContext } from '@/providers/KpiProvider';
import { useLanguage } from '@/providers/LanguageProvider';
import { Separator } from '@radix-ui/react-separator';
import { RefreshCcwIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAppDetails } from '@/hooks/useAppDetails';
import { useEvals } from '@/hooks/useEvals';

import { RadioOption } from '../combobox/enhanced-radio-select';
import { FilterPanel } from '../display-page/insights/filter-panel';
import { DateRange } from '../display-page/prompts/date-range-picker/calendar-helper';
import {
  getPresetLabelToNameMap,
  getPresetRange,
  usePresets,
} from '../display-page/prompts/date-range-picker/calendar-helper/presets';
import { DateRangePicker } from '../display-page/prompts/date-range-picker/date-range';
import { ChartDownload } from '../download/chart-download';
import { Skeleton } from '../ui/skeleton';
import FactsSelect from './facts-select';
import useKPI from './useKPI';
import { ViewBySelector } from './view-by-selector';

interface KPIItemsApplicationsProps {
  appName?: string;
  workflowName?: string;
  isLoading?: boolean;
  factName: string;
  handleFactNameChange: (value: string) => void;
  factsData: { key: string; label: string }[];
  selectedQualityFilter: RadioOption | null;
  setSelectedQualityFilter: Dispatch<SetStateAction<RadioOption | null>>;
  onFilterApply?: (
    workflows: string | null,
    factFilter: string | null,
    factIds: string | null
  ) => void;
  currentWorkflows?: string | null;
  currentFactFilter?: string | null;
  currentFactIds?: string | null;
  activeRange?: {
    from: Date | null;
    to: Date | null;
  };
  filterCount: number;
  defaultFact: string;
  setDefaultFact: (factName: string) => void;
}

export const KPIItemsApplications: React.FC<KPIItemsApplicationsProps> = ({
  appName,
  workflowName,
  isLoading,
  factName,
  handleFactNameChange,
  factsData,
  selectedQualityFilter,
  setSelectedQualityFilter,
  onFilterApply,
  currentWorkflows,
  currentFactFilter,
  currentFactIds,
  activeRange,
  filterCount,
  defaultFact,
  setDefaultFact,
}) => {
  const { messages } = useLanguage();
  const langData = messages?.ApplicationForm;
  const [dateRange, setDateRange] = useState<DateRange>();
  const { data } = useAppDetails();

  const pathname = usePathname();
  const isComponentsPage = pathname.includes('/components');
  const componentData = messages?.ComponentsPage;
  const { handleDateRangeApply } = useKPI(appName, workflowName);
  const {
    refreshSankeyData,
    metaData,
    viewBy,
    setViewBy,
    sankeyData,
    sankeyDataLoading,
  } = useKPIContext();
  const [activePreset, setActivePreset] = useState<string | null>(null);

  const { state: evalState } = useEvals();
  const { evalTemplates, templateLoading, error } = evalState || {};

  const handleQualityFilterUpdate = (value: RadioOption | null) => {
    setSelectedQualityFilter(value);
  };

  // Get supported views dynamically based on current fact and sankey data
  const supportedViews = useMemo(() => {
    if (!metaData || !factName) return [];
    // wait for templates api to load
    if (templateLoading || (evalTemplates === null && !error)) return [];

    // collect names of all templates categorized as tests evals
    const testNames = new Set(
      evalTemplates
        ?.filter((evalTemplate: any) =>
          evalTemplate.category?.some(
            (cat: any) => cat?.toLowerCase() === 'test'
          )
        )
        ?.map((evalTemplate: any) => evalTemplate.name) || []
    );

    // collect names of all templates categorized as llm or manual evals
    const evalNames = new Set(
      evalTemplates
        ?.filter((evalTemplate: any) =>
          evalTemplate.category?.some(
            (cat: any) =>
              cat?.toLowerCase() === 'llm' || cat?.toLowerCase() === 'manual'
          )
        )
        ?.map((evalTemplate: any) => evalTemplate.name) || []
    );

    const viewsMap = new Map();

    // Iterate through ALL keys in metaData
    Object.values(metaData).forEach((nodeData) => {
      if (!Array.isArray(nodeData)) return;

      nodeData.forEach((item) => {
        if (typeof item === 'object' && item !== null) {
          Object.entries(item).forEach(([viewName, viewData]) => {
            if (!viewsMap.has(viewName)) {
              const allSubviews = Array.isArray(viewData)
                ? viewData.find((v) => v?.subviews)?.subviews
                : undefined;

              // exclude subviews that are categorized as tests evals unless explicitly included as evaluations(llm or manual)
              const subviews = Array.isArray(allSubviews)
                ? allSubviews.filter(
                    (subview: any) =>
                      !testNames.has(subview.name) ||
                      evalNames.has(subview.name)
                  )
                : undefined;

              viewsMap.set(viewName, subviews);
            }
          });
        }
      });
    });

    // populate Replay Tests perspective directly from templates list
    const replayTestSubviews = Array.from(testNames).map((name) => ({ name }));

    if (replayTestSubviews.length > 0) {
      viewsMap.set('Replay Tests', replayTestSubviews);
    }

    return Array.from(viewsMap.entries()).map(([viewName, subviews]) => {
      const viewObj = {
        key: viewName,
        label:
          (langData as Record<string, string>)?.[
            viewName.toLowerCase().replace(/\s+/g, '_')
          ] || viewName,
      };

      if (subviews?.length > 0) {
        return { ...viewObj, hasSubviews: true, subviews };
      }

      return { ...viewObj, hasSubviews: false };
    });
  }, [metaData, factName, langData, evalTemplates]);

  // reset view to All if selected view is not supported
  // for ex: Replay Tests is not supported when switching to Default Observation Window preset
  useEffect(() => {
    if (viewBy !== 'All' && supportedViews.length > 0) {
      if (!supportedViews.some((view) => view.key === viewBy)) {
        setViewBy('All');
        setSelectedQualityFilter(null);
      }
    }
  }, [supportedViews, viewBy, setViewBy, setSelectedQualityFilter]);

  const presets = usePresets();

  const setFactValue = (newFactName: string) => {
    handleFactNameChange(newFactName);
    setSelectedQualityFilter(null);
    setViewBy('All');
  };

  // Set initial date range on component mount
  useEffect(() => {
    const presetMap = getPresetLabelToNameMap(presets);
    const defaultPresetName = presetMap[String(data?.default_time_range)];
    if (!defaultPresetName) return;

    const range = getPresetRange(defaultPresetName, presets);
    setDateRange(range);
    handleDateRangeApply({ range }); // set initial date range in URL
    setActivePreset(defaultPresetName);
  }, [data?.default_time_range]);

  const onDateRangeChange = (range: { from?: Date; to?: Date }) => {
    const isAllTime = !range.from && !range.to;
    if (isAllTime) {
      setDateRange({ from: undefined, to: undefined });
      handleDateRangeApply({
        range: { from: undefined, to: undefined },
      });
      return;
    }

    const validRange: DateRange = {
      from: range.from || new Date(),
      to: range.to || range.from || new Date(),
    };

    setDateRange(validRange);
    handleDateRangeApply({ range: validRange });
  };

  const formatDate = (
    date: Date | undefined,
    locale: string = 'en-GB'
  ): string => {
    if (!date) return 'all time';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatFilename = (str: string): string => {
    return str.replace(/\s+/g, '').replace(/[^a-zA-Z0-9_-]/g, '');
  };

  const isAllTime = !dateRange?.from || !dateRange?.to;

  const filename = isAllTime
    ? 'traces_sankey_all_time'
    : `traces_sankey_${formatFilename(
        `${formatDate(dateRange.from)}_${formatDate(dateRange.to)}`
      )}`;

  const handleRefresh = () => {
    // Recalculate preset range on refresh to use the current time window and get an updated range;
    // for custom range, don't recalculate - use the existing selected range
    let finalRange = dateRange;
    if (activePreset) {
      finalRange = getPresetRange(activePreset, presets);
    }

    if (finalRange) {
      setDateRange(finalRange);
      handleDateRangeApply({ range: finalRange });
      refreshSankeyData(factName, finalRange);
    }
  };

  useEffect(() => {
    const handleEvalFinished = () => {
      refreshSankeyData(factName, dateRange);
    };

    window.addEventListener('evalFromInsightsFinished', handleEvalFinished);

    return () => {
      window.removeEventListener(
        'evalFromInsightsFinished',
        handleEvalFinished
      );
    };
  }, [factName, dateRange, refreshSankeyData]);

  return (
    <div className="mt-8 flex items-center justify-between">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-h2 text-body-primary">
            {isComponentsPage
              ? `${componentData?.component_insights}`
              : `${messages?.ApplicationDetailPage?.application_insights}`}
          </h2>

          {!isLoading && (
            <button onClick={handleRefresh} className="cursor-pointer">
              <RefreshCcwIcon className="size-4 text-[#257C9D]" />
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-[38px] items-center justify-center">
          <Skeleton className="animate-pulse-custom h-6 w-[678px] rounded-full bg-[#8C9EA1]" />
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <FactsSelect
            factName={factName}
            factsData={factsData}
            defaultFact={defaultFact}
            setDefaultFact={setDefaultFact}
            setFactValue={setFactValue}
          />

          <div className="flex h-7 items-center rounded-e rounded-s border-2 border-form-control-border-rest px-3 py-1">
            <DateRangePicker
              onUpdate={(payload) => {
                onDateRangeChange({
                  from: payload.range?.from,
                  to: payload.range?.to,
                });
                setActivePreset(payload.presetName || null);
              }}
              classNames={{
                rangeTriggerClassName:
                  'border-none py-0 focus:ring-0 focus:ring-offset-0',
                triggerIconClassName: 'items-center gap-1.5',
                rangeClassName: cn(
                  'text-form-input-disabled text-[13px] font-normal items-center gap-1.5',
                  'flex-row'
                ),
              }}
              currentPage="insights"
              initialRange={dateRange}
            />
          </div>

          <Separator
            className="mx-2 h-7 w-[1px] bg-line-tertiary"
            orientation="vertical"
          />

          {supportedViews.length > 0 &&
            sankeyData &&
            Object.keys(sankeyData).length > 0 &&
            !sankeyDataLoading && (
              <>
                <ViewBySelector
                  viewBy={viewBy}
                  setViewBy={setViewBy}
                  supportedViews={supportedViews}
                  selectedQualityFilter={selectedQualityFilter}
                  setSelectedQualityFilter={setSelectedQualityFilter}
                  onQualityFilterUpdate={handleQualityFilterUpdate}
                />

                <Separator
                  className="h-7 w-[1px] bg-line-tertiary"
                  orientation="vertical"
                />
              </>
            )}
          {!workflowName && onFilterApply && (
            <div className="relative">
              <FilterPanel
                onApply={onFilterApply}
                currentWorkflows={currentWorkflows || null}
                currentFactFilter={currentFactFilter || null}
                currentFactIds={currentFactIds || null}
                activeRange={activeRange}
                factsData={factsData}
              />

              {filterCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#257C9D] text-[10px] font-medium text-white">
                  {filterCount}
                </span>
              )}
            </div>
          )}
          <ChartDownload chartId="sankey-chart-svg" filename={filename} />
        </div>
      )}
    </div>
  );
};
